import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { match as Match } from "react-router-dom";
import { Location } from "history";
import { injectIntl, IntlShape } from "react-intl";
import { toast } from "react-toastify";
import { TableChangeParams } from "../../../../components/Table/types";
import { GroundGraphqlContextStore } from "../../../../lib/ground-aws-graphql-core";
import PageSubheader from "../../../../components/PageSubheader";
import { EnumPaths } from "../../../../utils/navigation";
import history from "../../../../history";
import Table from "../../../../components/Table";
import Dropdown from "../../../../components/Tailwind/Dropdown";
import images from "../../../../images";
import { getTranslation } from "../../../../utils/translation";
import ConfirmModal from "../../../../utils/modal/confirm";
import IntlMessages from "../../../../utils/messages";
import {
  displayDayDDMMYYYY_HHMM,
  DEFAULT_LIMIT,
} from "../../../../utils/config";
import {
  RoundForm,
  SearchableRoundFormsFilterInput,
} from "../../../../lib/ground-aws-graphql-core/models/Api/Inspection";
import Badge from "../../../../components/Tailwind/Badge";

interface Props {
  intl: IntlShape;
  match: Match<{ cid: string }>;
  location: Location<{
    pageIndex: number;
    limit: number;
    type: string;
  }>;
}

const ListRoundForms = (props: Props): JSX.Element => {
  const { intl, location, match } = props;
  const [loading, setLoading] = useState(false);

  const [showDeleteRoundFormModal, setShowDeleteRoundFormModal] =
    useState(false);

  const [roundFormId, setRoundFormId] = useState<number | undefined>();
  const [initialParams, setInitialParams] = useState<TableChangeParams | null>(
    location?.state
  );

  const listRoundForms = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.listRoundForms
  );
  const roundForms = GroundGraphqlContextStore.useStoreState(
    actions => actions.inspection.roundForms.items
  );
  const total = GroundGraphqlContextStore.useStoreState(
    actions => actions.inspection.roundForms.total
  );
  const setRoundForm = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.setRoundForm
  );
  const centerTimezone = GroundGraphqlContextStore.useStoreState(
    state => state.center.centerTimezone
  );
  const deleteRoundForm = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.deleteRoundForm
  );

  useEffect(() => {
    if (location?.state)
      fetchData(location.state.pageIndex, true, location.state.limit);
    else fetchData();
  }, []);

  const fetchData = (pageIndex = 0, loader = true, limit = DEFAULT_LIMIT) => {
    if (loader) setLoading(true);

    const filter: SearchableRoundFormsFilterInput = {
      building_id: { eq: match.params.cid },
    };

    listRoundForms({ offset: pageIndex * limit, limit, filter }).finally(() =>
      setLoading(false)
    );
  };

  const addNewRoundForm = () =>
    history.push(
      `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.ROUND_FORMS}/${EnumPaths.CREATION_MODE}`
    );

  const handleOnChange = (changeParams: TableChangeParams) => {
    setInitialParams(changeParams);
    fetchData(changeParams.pageIndex, false, changeParams.limit);
  };

  const handleEditRoundForm = (id: number) => {
    setRoundForm(null);
    history.push(
      `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.ROUND_FORMS}/${id}/${EnumPaths.EDIT_MODE}`
    );
  };

  const openDeleteModal = (id: number) => {
    setRoundFormId(id);
    setShowDeleteRoundFormModal(true);
  };

  const closeDeleteModal = () => {
    setRoundFormId(undefined);
    setShowDeleteRoundFormModal(false);
  };

  const handleDelete = () => {
    if (!roundFormId) return;

    setLoading(true);
    setShowDeleteRoundFormModal(false);

    deleteRoundForm({ id: roundFormId })
      .then(() =>
        toast(
          intl.formatMessage({
            id: "page.inspection.round.form.delete.success",
          }),
          { type: "success" }
        )
      )
      .catch(() =>
        toast(
          intl.formatMessage({ id: "page.inspection.round.form.delete.error" }),
          { type: "error" }
        )
      )
      .finally(() => setLoading(false));
  };

  const tableHead = [
    "page.inspection.round.form.label",
    "page.inspection.round.form.checkpoints",
    "page.inspection.round.form.created_at",
    "page.inspection.round.form.updated_at",
    "page.inspection.round.form.created_by",
    "general.status",
    "general.actions",
  ];

  const tableBody = (roundForms || []).map((roundForm: RoundForm) => {
    const {
      id,
      title,
      created_at,
      updated_at,
      enabled,
      checkpoints,
      back_office_user,
    } = roundForm;
    const dropdownValues = [
      {
        id: "edit",
        label: "page.list.inspection.round.forms.edit",
        icon: images.edit,
        link: `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.ROUND_FORMS}/${id}/${EnumPaths.EDIT_MODE}`,
      },
      {
        id: "delete",
        label: "general.delete",
        icon: images.deleteIcon,
        onClick: () => openDeleteModal(id),
      },
    ];

    return {
      id: id.toString(),
      rowElements: [
        {
          element: getTranslation(title),
          onCellClick: () => handleEditRoundForm(id),
        },
        {
          element: checkpoints?.length,
          onCellClick: () => handleEditRoundForm(id),
        },
        {
          element: displayDayDDMMYYYY_HHMM(created_at, centerTimezone),
          onCellClick: () => handleEditRoundForm(id),
        },
        {
          element: displayDayDDMMYYYY_HHMM(updated_at, centerTimezone),
          onCellClick: () => handleEditRoundForm(id),
        },
        {
          element: `${back_office_user?.first_name} ${back_office_user?.last_name}`,
          onCellClick: () => handleEditRoundForm(id),
        },
        {
          element: enabled ? (
            <Badge label="general.actif" bgClassName="bg-ground-green-100" />
          ) : (
            <Badge label="general.inactif" bgClassName="bg-red-500" />
          ),
          onCellClick: () => handleEditRoundForm(id),
        },
        {
          element: (
            <Dropdown
              values={dropdownValues}
              dataCy={`inspection-round-form-dropdown-${id}`}
            />
          ),
        },
      ],
    };
  });

  return (
    <div>
      <Helmet>
        <title>
          {intl.formatMessage({
            id: "page.list.inspection.round.forms.document.title",
          })}
        </title>
      </Helmet>

      <PageSubheader
        title="page.list.inspection.round.forms.title"
        nbOfResults={total}
        buttonRightTitle="page.list.inspection.round.forms.create.round.form"
        buttonRightAction={addNewRoundForm}
        buttonRightId="btn-create-round-form"
      />

      <Table
        initialParams={initialParams}
        head={tableHead}
        body={tableBody}
        noDataText="page.list.inspection.round.forms.empty"
        onChange={handleOnChange}
        paginationLimit={DEFAULT_LIMIT}
        paginationTotal={total}
        loading={loading}
        setLoading={setLoading}
        className="px-8"
      />

      <ConfirmModal
        isOpen={showDeleteRoundFormModal}
        onRequestClose={closeDeleteModal}
        toggle={closeDeleteModal}
        handleConfirm={handleDelete}
        content={<IntlMessages id="page.list.inspection.round.forms.delete" />}
      />
    </div>
  );
};

export default injectIntl(ListRoundForms);
